<template>
  <v-row>
    <v-col cols="12">
      <v-progress-linear v-if="loading_templates" indeterminate color="primary" />

      <v-row class="cards-row" v-else>
        <v-col
          v-for="template in available_templates"
          :key="template.id"
          cols="12"
          :md="single_column ? 12 : 6"
        >
          <slot
            name="template-card"
            :template="template"
            :is_selected="selected_template_id === template.id"
            :comp_custom_fonts="comp_custom_fonts"
            :is_only_option="has_only_one_option"
            :on_select="() => onTemplateSelect(template)"
          >
            <div class="pa-4 text-center grey lighten-3">
              <p class="mb-0">{{ template.name || template.id }}</p>
              <p class="text-caption mb-0">Provide a template-card slot to render preview</p>
            </div>
          </slot>
        </v-col>
      </v-row>

      <v-row v-if="has_only_one_option" class="mt-4">
        <v-col cols="12" class="text-center text-caption font-weight-regular">
          <p class="mb-0">
            <strong>Note:</strong> Currently, only one template is available. More templates may be added in the future!
          </p>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
/**
 * Reusable gallery component for displaying and selecting widget templates.
 * Handles loading templates from backend with configurable endpoint and parameters.
 * Uses scoped slots to allow parent to customize template card rendering.
 *
 * @example
 * <widget-template-gallery
 *   api_endpoint="POST: /api/v1/guest-check-in/template-types"
 *   :build_api_params="buildParams"
 *   :selected_template_id="selected?.id"
 *   @template-selected="onSelect"
 *   @template-deselected="onDeselect"
 * >
 *   <template #template-card="{ template, is_selected, on_select }">
 *     <widget-template-card
 *       :template="template"
 *       :is_selected="is_selected"
 *       @select="on_select"
 *     />
 *   </template>
 * </widget-template-gallery>
 */
export default {
  name: 'WidgetTemplateGallery',

  props: {
    /**
     * API endpoint to fetch templates from
     * Format: "METHOD: /path"
     * Example: "POST: /api/v1/guest-check-in/template-types"
     */
    api_endpoint: {
      type: String,
      required: true,
    },

    /**
     * Function to build API request parameters
     * Signature: (component_context) => params_object
     * Receives 'this' context to access props/route/etc
     */
    build_api_params: {
      type: Function,
      default: () => ({}),
    },

    /**
     * Property path in API response that contains templates array
     * Uses slash notation for nested paths
     * Example: "template_types" or "templates" or "data/items"
     */
    templates_response_key: {
      type: String,
      default: 'template_types',
    },

    /**
     * Template IDs to exclude from display (e.g., 'blank')
     */
    excluded_template_ids: {
      type: Array,
      default: () => ['stub'],
    },

    /**
     * Currently selected template ID
     */
    selected_template_id: {
      type: String,
      default: null,
    },

    /**
     * Display templates in single column
     */
    single_column: {
      type: Boolean,
      default: true,
    },

  },

  data() {
    return {
      available_templates: [],
      loading_templates: false,
    };
  },

  async mounted() {
    await this.loadTemplates();
  },

  computed: {
    has_only_one_option() {
      return this.available_templates.length === 1;
    },
  },

  methods: {
    async loadTemplates() {
      this.loading_templates = true;

      const params = this.build_api_params(this);

      this.$rc.rest({
        spinner: false,
        endpoint: this.api_endpoint,
        params,

        on_success: data => {
          // extract templates from response using configured key path
          const templates = this.$rc.getDeep(data, this.templates_response_key);

          this.available_templates = templates.filter(
            template => !this.excluded_template_ids.includes(template.id)
          );

          this.comp_custom_fonts = data.comp_custom_fonts;

          this.loading_templates = false;
          this.$emit('templates-loaded', this.available_templates);

          // auto select if only one template available
          if (this.available_templates.length === 1 && !this.selected_template_id) {
            this.$emit('template-selected', this.available_templates[0]);
          }
        },

        on_error: error => {
          this.loading_templates = false;
          this.$rc.alert('Unable to load templates');
          this.$emit('load-error', error);
        },
      });
    },

    onTemplateSelect(template) {
      if (this.selected_template_id === template.id) {
        this.$emit('template-deselected');
      } else {
        this.$emit('template-selected', template);
      }
    },

    /**
     * Public method to manually reload templates
     */
    reload() {
      this.loadTemplates();
    },
  },
};
</script>

<style scoped lang="scss">
.row {
  margin: 0;
}
.cards-row {
  row-gap: 2.5rem;
  justify-content: center;
}
</style>
